import React, { createContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, register as apiRegister, getCurrentUser } from '../services/api';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, if a token exists, try to fetch the current user.
  // If the fetch fails (e.g. expired token), clear the stale token.
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }
    getCurrentUser()
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem('auth_token');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Clear user state when api.js fires 'auth:unauthorized' (a soft
  // 401 signal that avoids full-page reloads). Navigation to /login is
  // handled by AuthRedirectHandler in App.js which has access to React
  // Router's navigate(). See #173.
  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  // Cross-tab auth sync. localStorage writes in one tab fire a
  // `storage` event in every OTHER tab, so if the user logs out in
  // tab A, tab B can see that and wipe its in-memory user state. Same
  // for login (if the token appears, refetch /auth/me). Without this
  // listener, tabs drift out of sync: a tab that hasn't reloaded
  // since a different tab logged out will keep showing the
  // authenticated UI and making authenticated requests (which
  // promptly 401 because the interceptor already cleared the token).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'auth_token') return;
      if (!e.newValue) {
        // Token removed in another tab → log out here too.
        setUser(null);
      } else if (e.newValue !== e.oldValue) {
        // Token changed in another tab → refetch the user.
        getCurrentUser()
          .then((u) => setUser(u))
          .catch(() => setUser(null));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback(async (email, password) => {
    const { access_token } = await apiLogin(email, password);
    localStorage.setItem('auth_token', access_token);
    const me = await getCurrentUser();
    setUser(me);
    return me;
  }, []);

  const register = useCallback(async (email, password, fullName) => {
    await apiRegister(email, password, fullName);
    // Auto-login after successful registration
    return login(email, password);
  }, [login]);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return ctx;
}
